import { useMemo } from "react";
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { BaseWidget } from "./BaseWidget";
import { MonthYearControl } from "../controls/MonthYearControl";
import { FirestoreSelectControl } from "../controls/FirestoreSelectControl";
import { useFirestoreGeneral } from "../../../../firebase/useFirestore";
import useSettings from "../../../../hooks/Settings";
import usePersonaPresenceReports from "../../../../hooks/usePersonaPresenceReports";
import presenceAttendanceStats from "../../../../helpers/personas/presenceAttendanceStats";
import PersonasPresenceVisualization from "../../../dataVisualizations/PersonasPresenceVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";
import { recordInLocation } from '../../../../helpers/locations/locationFilter'
import { useWithPersonaLocations } from '../../../../hooks/usePersonaLocations';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// PersonasPresenceWidget — attendance percentage + per-persona breakdown
// for a single month. Sidebar controls own the month picker and (optional)
// location filter; the rendered visualisation is read-only (no in-body
// select), per the published-report constraint.


/**
 * Parse a 'YYYY-MM' month string. Returns { month: 1-12, year } or null when
 * the input is missing / malformed.
 *
 * @param {string|null|undefined} monthStr
 * @returns {{ month: number, year: number } | null}
 */
const parseMonthString = (monthStr) => {
    if (!monthStr || typeof monthStr !== 'string') return null;
    const m = monthStr.match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) return null;
    return { month, year };
};

/**
 * Get the current month as 'YYYY-MM'.
 */
const currentMonthString = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * @extends {BaseWidget}
 */
export class PersonasPresenceWidget extends BaseWidget {

    getName() { return 'Aanwezigheid'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <EventAvailableOutlinedIcon />; }

    getControls() {
        return {
            'month': new MonthYearControl({
                label: 'Maand',
                description: 'Kies de maand waarvoor de aanwezigheid wordt getoond. Leeg = huidige maand.',
                defaultValue: null,
            }),
            'locationFilter': new FirestoreSelectControl({
                label: 'Locatie',
                description: 'Filter op locatie. Leeg = alle locaties.',
                firestore: {
                    collection: 'Locations',
                    valueField: 'docid',
                    labelField: 'Title',
                    orderBy: 'Position',
                    includeAll: true,
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string|null} [props.month] - 'YYYY-MM' or null (= current month)
 * @param {string} [props.locationFilter]
 * @returns {JSX.Element}
 */
const Renderer = ({ month: monthSetting, locationFilter }) => {
    const { personasOn } = useSettings();
    const isTemplate = isTemplateMode();

    const parsed = parseMonthString(monthSetting) || parseMonthString(currentMonthString());
    const { month, year } = parsed || { month: 1, year: 2000 };

    const allPersonas = useWithPersonaLocations(
        useFirestoreGeneral('Personas', 'Status', 'active'),
    );
    const filteredPersonas = useMemo(() => {
        const arr = Array.isArray(allPersonas) ? allPersonas : [];
        if (!locationFilter) return arr;
        return arr.filter((p) => recordInLocation(p, locationFilter));
    }, [allPersonas, locationFilter]);

    // Skip the network call while the placeholder is going to render anyway
    // (template-preview / module-disabled). Saves a roundtrip per editor
    // open on tenants without the Medewerkers module enabled.
    const reportsEnabled = !isTemplate && personasOn === 'flex';
    const reports = usePersonaPresenceReports(month, year, { enabled: reportsEnabled });

    // Zelfde rekenwerk als de dashboardkaart. Stond hier een eigen kopie die
    // alleen filterde wanneer er een locatiefilter aanstond, waardoor dezelfde
    // maand in de kaart en in het rapport een ánder percentage gaf.
    // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
    const { reports: filteredReports, percentage: attendancePercentage } = useMemo(
        () => presenceAttendanceStats(reports, filteredPersonas),
        [reports, filteredPersonas],
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (personasOn !== 'flex') {
        return <ModulePlaceholder mode="disabled" moduleLabel="Medewerkers" />;
    }

    return (
        <PersonasPresenceVisualization
            attendancePercentage={attendancePercentage}
            personas={filteredPersonas}
            month={month}
            year={year}
            presenceReports={filteredReports}
            /* No onMonthChange/onYearChange — selects rendered only in dashboard mode */
        />
    );
};

export { parseMonthString };
